import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message, Typography } from 'antd';
import { postCall } from '../../utils/network.util';
import { SEND_UPI_PERMISSION_OTP_URL, VERIFY_UPI_PERMISSION_OTP_URL } from '../../utils/api-url';
import { isValid } from '../../utils/utilities';

const { Text } = Typography;

/*
  UPI permission OTP gate.

  When an operator enables REQUIRE_UPI_CANCELLATION_OTP, cancelling / bus-cancelling /
  refunding a UPI-booked ticket must first be authorised with an OTP sent to the
  operator's UPI_PERMISSION_NUMBER. This modal sends + verifies that OTP and calls
  onVerified() only after a successful verification.
*/

// True when this operator requires an OTP for UPI ticket cancel/refund.
export const isUpiOtpRequired = () =>
  isValid(global.travelsAppConfig) && global.travelsAppConfig['REQUIRE_UPI_CANCELLATION_OTP'] == 1;

// True when a ticket was booked via UPI (Razorpay).
export const isUpiBookedTicket = (paymentGateway) =>
  isValid(paymentGateway) && String(paymentGateway).toLowerCase() === 'razorpay';

// Convenience: does this action need OTP verification for the given ticket's gateway?
export const needsUpiOtp = (paymentGateway) => isUpiOtpRequired() && isUpiBookedTicket(paymentGateway);

export const UpiPermissionOtpModal = ({ visible, onVerified, onCancel, title = 'UPI Permission OTP', pnr, tripId }) => {
  const [otp, setOtp] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Locks the OTP to the thing being cancelled: PNR (ticket cancel) or trip id (bus cancel / refund).
  const otpContext = () => {
    const ctx = {};
    if (isValid(pnr)) { ctx.pnr = pnr; }
    if (isValid(tripId)) { ctx.tripId = tripId; }
    return ctx;
  };

  const sendOtp = () => {
    setSending(true);
    postCall(SEND_UPI_PERMISSION_OTP_URL, otpContext())
      .then((res) => {
        setSending(false);
        if (res && res.status === 'SUCCESS' && isValid(res.payload) && res.payload.Status === 'SUCCESS') {
          const to = isValid(res.payload.Payload) ? res.payload.Payload.sentTo : '';
          setSentTo(to);
          message.success(`OTP sent${to ? ' to ' + to : ''}`);
        } else {
          message.error(res && isValid(res.payload) && isValid(res.payload.Message) ? res.payload.Message : 'Failed to send OTP');
        }
      })
      .catch(() => { setSending(false); message.error('Failed to send OTP'); });
  };

  // Auto-send the OTP whenever the modal opens.
  useEffect(() => {
    if (visible) {
      setOtp('');
      setSentTo('');
      sendOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const verifyOtp = () => {
    if (!otp) {
      message.error('Please enter the OTP');
      return;
    }
    setVerifying(true);
    postCall(VERIFY_UPI_PERMISSION_OTP_URL, { otp, ...otpContext() })
      .then((res) => {
        setVerifying(false);
        if (res && res.status === 'SUCCESS' && isValid(res.payload) && res.payload.Status === 'SUCCESS') {
          setOtp('');
          onVerified();
        } else {
          message.error(res && isValid(res.payload) && isValid(res.payload.Message) ? res.payload.Message : 'Invalid OTP');
        }
      })
      .catch(() => { setVerifying(false); message.error('Failed to verify OTP'); });
  };

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={() => { setOtp(''); onCancel(); }}
      maskClosable={false}
      footer={[
        <Button key="resend" onClick={sendOtp} loading={sending}>Resend OTP</Button>,
        <Button key="verify" type="primary" loading={verifying} onClick={verifyOtp}>Verify &amp; Proceed</Button>
      ]}
    >
      <Text>
        This is a UPI booked ticket. An OTP has been sent to the registered UPI permission number
        {sentTo ? ` (${sentTo})` : ''}. Enter it to proceed.
      </Text>
      <Input
        style={{ marginTop: 12 }}
        placeholder="Enter OTP"
        value={otp}
        maxLength={6}
        onChange={(e) => setOtp(e.target.value)}
        onPressEnter={verifyOtp}
      />
    </Modal>
  );
};
